import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import { ShadeGrid } from "@/components/catalogue/ShadeGrid";
import { OrderTray } from "@/components/catalogue/OrderTray";
import { CompareBar, MAX_COMPARE } from "@/components/catalogue/CompareBar";
import { OrderDetailsDialog } from "@/components/catalogue/OrderDetailsDialog";
import { ReorderPanel } from "@/components/catalogue/ReorderPanel";
import { CutPieceOrder } from "@/components/catalogue/CutPieceOrder";
import { useShadeOrder } from "@/components/catalogue/useShadeOrder";
import { americanCrepeCharts, liningCharts, getCrepeChartUrl } from "@/data/galleryImages";
import { Search, ShoppingBag, Images, X, Columns2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_QUALITY = "600-lining";

// Restored orders only store the shade number, so rebuild the tile URL from the card
const shadeImageUrl = (shadeCard, number) =>
  shadeCard ? `/api/shades-static/${shadeCard}/${number}.webp` : undefined;

const STOCK_LABEL = {
  ready: "Ready stock",
  low: "Low stock",
  made_to_order: "Made to order · at least 25 days",
};

export default function ShadeCatalogue() {
  const { quality: qualityParam } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [trayOpen, setTrayOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [cardError, setCardError] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compare, setCompare] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [whatsappHref, setWhatsappHref] = useState("");
  const [buyer, setBuyer] = useState(null);

  const order = useShadeOrder();
  const activeId = qualityParam || DEFAULT_QUALITY;
  const product = products.find((p) => p.id === activeId);
  const isCutPiece = product?.category === "cut_piece";

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => {
        if (!r.ok) throw new Error(`products ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.products;
        if (!Array.isArray(list)) throw new Error("products payload is not a list");
        setProducts(list);
      })
      .catch((e) => {
        console.error("Products load failed:", e);
        setLoadError(true);
      });
  }, []);

  useEffect(() => {
    if (!product || isCutPiece) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setCard(null);
    setCardError(false);
    fetch(`${API}/shade-cards/${product.shade_card}`)
      .then((r) => {
        if (!r.ok) throw new Error(`shade card ${r.status}`);
        return r.json();
      })
      .then((data) => alive && setCard(data))
      .catch((e) => {
        console.error("Shade card load failed:", e);
        if (alive) setCardError(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [product, isCutPiece]);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setBuyer(d.user))
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.body.classList.add("catalogue-page");
    return () => document.body.classList.remove("catalogue-page");
  }, []);

  // Keep the selected quality visible in the horizontally-scrolling rail
  useEffect(() => {
    const pill = document.querySelector(`[data-testid="quality-pick-${activeId}"]`);
    if (pill) pill.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeId, products.length]);

  useEffect(() => {
    document.body.classList.toggle("tray-open", trayOpen);
    return () => document.body.classList.remove("tray-open");
  }, [trayOpen]);

  // The compare bar occupies the bottom-left on phones, where the advisor launcher sits
  useEffect(() => {
    document.body.classList.toggle("compare-open", compare.length > 0 && !trayOpen);
    return () => document.body.classList.remove("compare-open");
  }, [compare.length, trayOpen]);

  useEffect(() => {
    setQuery("");
    setCompare([]);
    setCompareMode(false);
  }, [activeId]);

  useEffect(() => {
    if (!chartsOpen) return;
    const onKey = (e) => e.key === "Escape" && setChartsOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [chartsOpen]);

  const shades = useMemo(() => {
    if (!card) return [];
    const q = query.trim();
    return q ? card.shades.filter((s) => String(s.number).includes(q)) : card.shades;
  }, [card, query]);

  const toggleShade = useCallback(
    (shade) => {
      const key = `${product.id}|${shade.number}`;
      if (order.items.some((i) => i.key === key)) {
        order.remove(key);
        return;
      }
      order.add({
        quality_id: product.id,
        quality_name: product.name,
        number: shade.number,
        hex: shade.hex,
        image_url: shade.image_url,
        than_meters: product.than_meters,
      });
    },
    [order, product]
  );

  const toggleCompare = useCallback((shade) => {
    setCompare((prev) => {
      if (prev.some((s) => s.number === shade.number)) {
        return prev.filter((s) => s.number !== shade.number);
      }
      if (prev.length >= MAX_COMPARE) return [...prev.slice(1), shade];
      return [...prev, shade];
    });
  }, []);

  const onTileTap = useCallback(
    (shade) => (compareMode ? toggleCompare(shade) : toggleShade(shade)),
    [compareMode, toggleCompare, toggleShade]
  );

  const addFromCompare = useCallback(
    (shade) => {
      toggleShade(shade);
    },
    [toggleShade]
  );

  const comparePinned = useCallback(
    (number) => order.items.some((i) => i.key === `${activeId}|${number}`),
    [order.items, activeId]
  );

  // Pull a previous order back into the tray, re-pricing it against today's products
  const loadPreviousOrder = useCallback(
    (previousItems) => {
      if (
        order.items.length &&
        !window.confirm(
          `This will replace the ${order.items.length} line${order.items.length === 1 ? "" : "s"} already in your order. Continue?`
        )
      ) {
        return null;
      }
      order.clear();
      previousItems.forEach((i) => {
        const p = products.find((x) => x.id === i.quality_id);
        order.add({
          quality_id: i.quality_id,
          quality_name: p?.name || i.quality_name,
          number: i.number ?? undefined,
          cut_length: i.cut_length ?? undefined,
          thans: i.thans,
          than_meters: p?.than_meters ?? i.than_meters,
          pieces_per_pack: i.pieces_per_pack ?? p?.pieces_per_pack,
          hex: i.hex ?? undefined,
          image_url: i.number != null ? shadeImageUrl(p?.shade_card, i.number) : undefined,
        });
      });
      setTrayOpen(true);
      return previousItems.length;
    },
    [order, products]
  );

  const charts = product?.shade_card === "crepe" ? americanCrepeCharts : liningCharts;

  return (
    <PageLayout>
      <SEO
        title={`${product?.name || "Shade"} Catalogue — Order Lining Shades | Mutha Paras & Co.`}
        description="Browse every shade in our lining and American Crepe shade cards from real swatch photos, build your order by than, and send it straight to us on WhatsApp."
        path={`/catalogue/${activeId}`}
      />

      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4 mb-7">
          <div>
            <p
              className="text-[10px] sm:text-xs tracking-[0.2em] uppercase mb-2"
              style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
            >
              Shade Catalogue
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              data-testid="catalogue-heading"
            >
              Pick your quality, then your shades
            </h1>
            <p
              className="text-sm md:text-base mt-3 max-w-2xl"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.7 }}
            >
              Every tile below is a photograph of the actual dyed fabric from our swatch book — no
              guesswork from a screen colour.
            </p>
            <div className="mt-4 max-w-sm">
              {buyer ? (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
                  style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="catalogue-my-orders-link"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Your past orders
                </Link>
              ) : (
                <ReorderPanel onLoad={loadPreviousOrder} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quality rail — outside the header section so it stays stuck while the grid scrolls */}
      <div
        className="sticky top-20 lg:top-24 z-30 border-y backdrop-blur-md"
        style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-paper)" }}
      >
        {/* Margin (not padding) so the scroller's clip edge keeps the pills clear of the fixed tray */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-3">
          <div
            className="flex gap-2 overflow-x-auto no-scrollbar lg:mr-[22.5rem] xl:mr-[23.5rem]"
            data-testid="quality-rail"
          >
            {products.map((p) => {
              const active = p.id === activeId;
              return (
                <button
                  key={p.id}
                  onClick={() => navigate(`/catalogue/${p.id}`)}
                  data-testid={`quality-pick-${p.id}`}
                  className="shrink-0 px-4 py-2.5 rounded-full text-xs sm:text-sm font-medium border transition-colors whitespace-nowrap"
                  style={{
                    borderColor: active ? "var(--mp-copper)" : "var(--mp-line)",
                    backgroundColor: active ? "var(--mp-copper-solid)" : "transparent",
                    color: active ? "#FFFFFF" : "var(--mp-text)",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-28 lg:pb-16">
        {loadError && (
          <p
            className="py-20 text-center text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="catalogue-load-error"
          >
            We couldn't load the catalogue just now. Please refresh, or message us on WhatsApp and
            we'll send the shade card across.
          </p>
        )}
        {!loadError && products.length === 0 && (
          <p
            className="py-20 text-center text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="catalogue-loading"
          >
            Loading the shade card&hellip;
          </p>
        )}
        {products.length > 0 && !product && (
          <p
            className="py-20 text-center text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="quality-not-found"
          >
            That quality isn't on our range. Pick one from the list above.
          </p>
        )}
        {cardError && product && (
          <p
            className="py-20 text-center text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="shade-card-error"
          >
            We couldn't load the shade card for {product.name}. Please refresh, or message us on
            WhatsApp and we'll send it across.
          </p>
        )}
        {product && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6 text-xs" data-testid="quality-meta">
            <Meta label="Panna" value={`${product.panna} · ${product.width}`} />
            {!isCutPiece && <Meta label="Than" value={`${product.than_meters} metres`} />}
            {isCutPiece ? (
              <Meta label="Pack" value={`${product.pieces_per_pack} colours`} />
            ) : (
              <Meta label="Shades" value={`${product.colors_available} on card`} />
            )}
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-medium"
              style={{
                backgroundColor: product.stock_status === "ready" ? "var(--mp-copper)" : "var(--mp-line)",
                color: product.stock_status === "ready" ? "#FFFFFF" : "var(--mp-text)",
                fontFamily: "'Manrope', sans-serif",
              }}
              data-testid="quality-stock-badge"
            >
              {STOCK_LABEL[product.stock_status] || product.stock_status}
            </span>
            {!isCutPiece && (
              <button
                onClick={() => setChartsOpen(true)}
                data-testid="view-printed-charts"
                className="inline-flex items-center gap-1.5 text-[11px] underline underline-offset-4 transition-opacity hover:opacity-70"
                style={{ color: "var(--mp-copper-deep)", fontFamily: "'Manrope', sans-serif" }}
              >
                <Images className="w-3.5 h-3.5" /> View printed charts
              </button>
            )}
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-10 lg:items-start">
          <div className="min-w-0">
            {isCutPiece ? (
              <CutPieceOrder product={product} onAdd={order.add} />
            ) : product ? (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-sm border w-full sm:w-64"
                    style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
                  >
                    <Search className="w-4 h-4 shrink-0" style={{ color: "var(--mp-muted)" }} />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      inputMode="numeric"
                      placeholder="Search shade number"
                      data-testid="shade-search"
                      className="w-full min-w-0 text-sm bg-transparent outline-none"
                      style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                    />
                  </div>
                  <p
                    className="text-xs"
                    style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="shade-count"
                  >
                    {shades.length} of {card?.count || 0} shades
                  </p>
                  <button
                    onClick={() => setCompareMode((v) => !v)}
                    data-testid="compare-mode-toggle"
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm border transition-colors"
                    style={{
                      borderColor: compareMode ? "var(--mp-copper)" : "var(--mp-line)",
                      color: compareMode ? "var(--mp-copper)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                  >
                    <Columns2 className="w-3.5 h-3.5" />
                    {compareMode ? "Comparing — tap to stop" : "Compare shades"}
                  </button>
                </div>

                {loading ? (
                  <p
                    className="py-20 text-center text-sm"
                    style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="shade-grid-loading"
                  >
                    Loading shade card…
                  </p>
                ) : (
                  <ShadeGrid
                    shades={shades}
                    qualityId={activeId}
                    isPinned={compareMode ? (q, n) => compare.some((s) => s.number === n) : order.isPinned}
                    onToggle={onTileTap}
                  />
                )}
              </>
            ) : null}
          </div>

          <OrderTray
            items={order.items}
            totals={order.totals}
            onSetThans={order.setThans}
            onRemove={order.remove}
            onClear={order.clear}
            open={trayOpen}
            onClose={() => setTrayOpen(false)}
            onSubmit={(href) => {
              setWhatsappHref(href);
              setDetailsOpen(true);
            }}
            reference={reference}
          />
          <div className="hidden lg:block" aria-hidden="true" />
        </div>
      </section>

      <CompareBar
        shades={compare}
        qualityName={product?.name || ""}
        onRemove={(number) => setCompare((prev) => prev.filter((s) => s.number !== number))}
        onClear={() => setCompare([])}
        onAdd={addFromCompare}
        isPinned={comparePinned}
        hidden={trayOpen || detailsOpen}
      />

      <OrderDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        items={order.items}
        totals={order.totals}
        whatsappHref={whatsappHref}
        buyer={buyer}
        onSent={(ref) => {
          setReference(ref);
          order.clear();
        }}
      />

      {/* Mobile tray trigger — portaled for the same containing-block reason as the tray */}
      {!trayOpen &&
        createPortal(
          <button
            onClick={() => setTrayOpen(true)}
            data-testid="open-order-tray"
            className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex items-center justify-between px-6 py-4"
            style={{ backgroundColor: "var(--mp-ink)", color: "#FAF7F0", fontFamily: "'Manrope', sans-serif" }}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <ShoppingBag className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
              {order.totals.shades} {order.totals.shades === 1 ? "line" : "lines"}
            </span>
            <span className="text-sm font-semibold" style={{ color: "var(--mp-copper)" }}>
              Review order
            </span>
          </button>,
          document.body
        )}

      {chartsOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[70] overflow-y-auto"
            style={{ backgroundColor: "rgba(16,16,16,0.96)" }}
            role="dialog"
            aria-modal="true"
            aria-label={`Printed shade charts for ${product?.name || "this quality"}`}
            data-testid="charts-modal"
          >
            <div
              className="sticky top-0 flex items-center justify-between px-5 py-4"
              style={{ backgroundColor: "var(--mp-ink)" }}
            >
              <p className="text-sm" style={{ color: "#FAF7F0", fontFamily: "'Manrope', sans-serif" }}>
                Printed shade charts — {product?.name}
              </p>
              <button
                onClick={() => setChartsOpen(false)}
                data-testid="charts-modal-close"
                aria-label="Close charts"
                className="p-2"
                style={{ color: "#FAF7F0" }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
              {charts.map((c) => (
                <img
                  key={c.file}
                  src={getCrepeChartUrl(c.file).url}
                  alt={c.label}
                  loading="lazy"
                  className="w-full rounded-sm"
                  data-testid={`chart-image-${c.file}`}
                />
              ))}
            </div>
          </div>,
          document.body
        )}
    </PageLayout>
  );
}

const Meta = ({ label, value }) => (
  <span style={{ fontFamily: "'Manrope', sans-serif" }}>
    <span style={{ color: "var(--mp-muted)" }}>{label}: </span>
    <span className="font-semibold" style={{ color: "var(--mp-text)" }}>{value}</span>
  </span>
);

